// DataContext for managing reports data in Cleancy Web App
// 3-tier data flow (minimal | preview | full) ported from the cleancy mobile
// app: cold-load status-only pins, lazy-load preview per visible list row, load
// full data on select, and overlay status-only re-syncs without downgrading.

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { DataContextType, ReportData } from "../types/global.d";
import {
  fetchStatusOnlySync,
  getReportById,
  getReportsByIds,
  getUserReports,
  SyncResponse,
} from "../api/sync";
import { API_CONFIG } from "../constants";
import { normalizeReportsTextFields } from "../utils/textNormalization";
import {
  deserializeReportsCache,
  mergeStatusOnly,
  serializeReportsCache,
  stampDataLevel,
  upgradeReports,
} from "../utils/reportsMerge";

// Create the context with default values
const DataContext = createContext<DataContextType>({
  reports: [],
  setReports: () => {},
  updateData: async () => {},
  loading: false,
  error: null,
  loadReportDetails: async () => null,
  getReportById: () => undefined,
  getUserReportsWithCache: async () => [],
  loadVisibleReports: () => {},
  requestStatusSync: () => {},
});

interface DataProviderProps {
  children: ReactNode;
}

/** Flatten a sync response's `reports` (array, or optimal nearby/distant). */
const extractReports = (response: SyncResponse): ReportData[] => {
  const reports = response.reports;
  if (Array.isArray(reports)) {
    return reports;
  }
  if (reports && typeof reports === "object" && "nearby" in reports) {
    return [...(reports.nearby || []), ...(reports.distant || [])];
  }
  return [];
};

const chunk = <T,>(items: T[], size: number): T[][] => {
  const batches: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    batches.push(items.slice(i, i + size));
  }
  return batches;
};

/**
 * DataProvider — fetches status-only pins on mount (after hydrating the
 * localStorage cache for instant paint), lazy-loads richer tiers on demand,
 * and applies tier-aware merges so a status overlay never clobbers loaded data.
 */
export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const [reports, setReports] = useState<ReportData[]>([]);
  const reportsRef = useRef<ReportData[]>(reports);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cacheTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncCoalesceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSyncRef = useRef<string | null>(null);
  const hydratedRef = useRef(false);

  // Preview lazy-load priority queue.
  const pendingQueueRef = useRef<Set<string>>(new Set());
  const inFlightRef = useRef<Set<string>>(new Set());
  const processingRef = useRef(false);

  // User reports caching to prevent duplicate API calls
  const userReportsCache = useRef<Map<string, ReportData[]>>(new Map());
  const pendingUserRequests = useRef<Map<string, Promise<ReportData[]>>>(
    new Map()
  );

  // Keep reportsRef in sync + persist a debounced status-only cache snapshot.
  useEffect(() => {
    reportsRef.current = reports;

    if (cacheTimeoutRef.current) {
      clearTimeout(cacheTimeoutRef.current);
    }
    cacheTimeoutRef.current = setTimeout(() => {
      try {
        if (reports.length) {
          localStorage.setItem(
            API_CONFIG.REPORTS_CACHE_KEY,
            serializeReportsCache(reports, lastSyncRef.current)
          );
        }
      } catch {
        // Ignore quota / serialization errors — the cache is best-effort.
      }
    }, 500);
  }, [reports]);

  const getReportByIdHelper = useCallback(
    (reportId: string): ReportData | undefined =>
      reportsRef.current.find((r) => r._id === reportId),
    []
  );

  /** Apply a status-only payload as a pin-status overlay (tier-aware merge). */
  const applyStatusOnlyResponse = useCallback((response: SyncResponse) => {
    const incoming = extractReports(response).map(stampDataLevel);
    if (response.serverTime) {
      lastSyncRef.current = response.serverTime;
    }
    setReports((prev) =>
      mergeStatusOnly(prev, incoming, {
        isIncremental: !!response.isIncremental,
        removedReports: response.removedReports,
      })
    );
  }, []);

  /**
   * Cold load — status + coordinates only for ALL reports (map pins). Preview
   * data is lazy-loaded for visible list rows; full data is loaded on select.
   */
  const updateData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchStatusOnlySync();
      applyStatusOnlyResponse(response);

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    } catch (err: any) {
      setError(err?.message || "Failed to fetch reports");
      retryTimeoutRef.current = setTimeout(() => {
        updateData();
      }, API_CONFIG.RETRY_TIMEOUT);
    } finally {
      setLoading(false);
    }
  }, [applyStatusOnlyResponse]);

  /** Coalesced status-only re-sync (post-mutation refresh / periodic poll). */
  const requestStatusSync = useCallback(() => {
    if (syncCoalesceRef.current) {
      clearTimeout(syncCoalesceRef.current);
    }
    syncCoalesceRef.current = setTimeout(async () => {
      syncCoalesceRef.current = null;
      try {
        const response = await fetchStatusOnlySync();
        applyStatusOnlyResponse(response);
      } catch (err) {
        console.error("Status re-sync failed:", err);
      }
    }, API_CONFIG.SYNC_COALESCE_MS);
  }, [applyStatusOnlyResponse]);

  /** Drain the preview queue: batch ids and upgrade them via /reports/bulk. */
  const processQueue = useCallback(async () => {
    if (processingRef.current || pendingQueueRef.current.size === 0) {
      return;
    }
    processingRef.current = true;

    try {
      const queued = Array.from(pendingQueueRef.current);
      pendingQueueRef.current.clear();

      const toLoad = queued.filter((id) => {
        if (inFlightRef.current.has(id)) return false;
        const report = reportsRef.current.find((r) => r._id === id);
        // The list only needs the preview tier — minimal rows still need it;
        // preview/full rows already have what the list shows.
        return (
          !!report &&
          report.dataLevel !== "full" &&
          report.dataLevel !== "preview"
        );
      });

      if (!toLoad.length) {
        return;
      }
      toLoad.forEach((id) => inFlightRef.current.add(id));

      const batches = chunk(toLoad, API_CONFIG.PREVIEW_BATCH_SIZE);
      const loaded: ReportData[] = [];
      for (let i = 0; i < batches.length; i += API_CONFIG.PREVIEW_MAX_CONCURRENT) {
        const slice = batches.slice(i, i + API_CONFIG.PREVIEW_MAX_CONCURRENT);
        const results = await Promise.all(
          slice.map((batch) =>
            // Preview tier only (single imagePreview) for list rows — full data
            // (all images, comment, cleaned) is loaded when a report is opened.
            getReportsByIds(batch, { preview: true }).catch((batchError) => {
              console.error("Preview batch failed:", batchError);
              return [] as ReportData[];
            })
          )
        );
        results.forEach((batchReports) => loaded.push(...batchReports));
      }

      if (loaded.length) {
        setReports((prev) => upgradeReports(prev, loaded));
      }
      toLoad.forEach((id) => inFlightRef.current.delete(id));
    } finally {
      processingRef.current = false;
      if (pendingQueueRef.current.size > 0) {
        setTimeout(() => processQueue(), 50);
      }
    }
  }, []);

  /** Queue preview/full loading for (visible) report ids. */
  const loadVisibleReports = useCallback(
    (reportIds: string[], highPriority = false) => {
      let added = false;
      reportIds.forEach((id) => {
        if (inFlightRef.current.has(id) || pendingQueueRef.current.has(id)) {
          return;
        }
        const report = reportsRef.current.find((r) => r._id === id);
        if (
          !report ||
          report.dataLevel === "full" ||
          report.dataLevel === "preview"
        ) {
          return;
        }
        pendingQueueRef.current.add(id);
        added = true;
      });

      if (!added) {
        return;
      }
      setTimeout(() => processQueue(), highPriority ? 0 : 100);
    },
    [processQueue]
  );

  /**
   * Load full details for a report (on select). No-ops when already full.
   */
  const loadReportDetails = useCallback(
    async (
      reportId: string,
      forceReload = false
    ): Promise<ReportData | null> => {
      const existing = reportsRef.current.find((r) => r._id === reportId);
      if (!forceReload && existing?.dataLevel === "full") {
        return existing;
      }

      try {
        const fetched = await getReportById(reportId);
        // The /:id endpoint returns the authoritative full payload.
        const fullReport: ReportData = {
          ...fetched,
          dataLevel: "full",
          isMinimal: false,
        };
        setReports((prev) => upgradeReports(prev, [fullReport]));
        return fullReport;
      } catch (err: any) {
        setError(err?.message || "Failed to load report details");
        return null;
      }
    },
    []
  );

  /** Get a user's reports with caching + in-flight de-duplication. */
  const getUserReportsWithCache = useCallback(
    async (nickname: string): Promise<ReportData[]> => {
      const cached = userReportsCache.current.get(nickname);
      if (cached) {
        return cached;
      }

      const pending = pendingUserRequests.current.get(nickname);
      if (pending) {
        return pending;
      }

      const requestPromise = getUserReports(nickname)
        .then((userReports) => {
          const processed = userReports.map((report) => ({
            ...report,
            dataLevel: "full" as const,
            isMinimal: false,
          }));
          userReportsCache.current.set(nickname, processed);
          pendingUserRequests.current.delete(nickname);
          return processed;
        })
        .catch((err) => {
          console.error(`Failed to fetch user reports for ${nickname}:`, err);
          pendingUserRequests.current.delete(nickname);
          throw err;
        });

      pendingUserRequests.current.set(nickname, requestPromise);
      return requestPromise;
    },
    []
  );

  /** Public reports setter that also clears errors. */
  const setReportsWithErrorClear = useCallback((newReports: ReportData[]) => {
    setReports(normalizeReportsTextFields(newReports));
    setError(null);
  }, []);

  // Hydrate the cache for instant pins, then fetch fresh data on mount.
  useEffect(() => {
    if (!hydratedRef.current) {
      hydratedRef.current = true;
      try {
        const cached = deserializeReportsCache(
          localStorage.getItem(API_CONFIG.REPORTS_CACHE_KEY)
        );
        if (cached?.reports.length) {
          lastSyncRef.current = cached.lastSync;
          setReports(cached.reports);
        }
      } catch {
        // Best-effort hydration only.
      }
    }

    updateData();

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      if (cacheTimeoutRef.current) {
        clearTimeout(cacheTimeoutRef.current);
        cacheTimeoutRef.current = null;
      }
      if (syncCoalesceRef.current) {
        clearTimeout(syncCoalesceRef.current);
        syncCoalesceRef.current = null;
      }
    };
  }, [updateData]);

  const contextValue: DataContextType = {
    reports,
    setReports: setReportsWithErrorClear,
    updateData,
    loading,
    error,
    loadReportDetails,
    getReportById: getReportByIdHelper,
    getUserReportsWithCache,
    loadVisibleReports,
    requestStatusSync,
  };

  return (
    <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>
  );
};

/**
 * Custom hook to use the DataContext
 * Throws an error if used outside of DataProvider
 */
export const useDataContext = (): DataContextType => {
  const context = useContext(DataContext);

  if (!context) {
    throw new Error("useDataContext must be used within a DataProvider");
  }

  return context;
};
